import moment, {
  type Duration,
  type Moment,
  type MomentInput,
} from "moment-timezone";

// A 12 hour datetime format, e.g. 'Nov 12th, 2019 11:28 am'.
export const DEFAULT_TIME_FORMAT = "MMM Do, YYYY hh:mm a";
export const DEFAULT_TIME_FORMAT_WITH_TZ = "MMM Do, YYYY hh:mm a z";
export const TIME_FORMAT_WITH_DAY = "ddd, MMM Do, YYYY hh:mm a";

export function calculateTimeDiff(timeDiff: Duration | null | undefined) {
  if (!timeDiff) {
    return "N/A";
  }

  const hours = Number(timeDiff.asHours().toFixed(1));
  const absHours = Math.abs(hours);
  if (absHours === 0) {
    return "Your Time";
  }

  const suffix = hours >= 0 ? "ahead" : "behind";

  return `${absHours}h ${suffix}`;
}

export function dateStringToDateFormat(
  dateStr: MomentInput,
  format = DEFAULT_TIME_FORMAT,
): string {
  return moment(dateStr).format(format);
}

/**
 * Calculates a new time based on a calculated duration from current time.
 * @param {Duration} difference Moment duration used to calculate time
 * @param {string} format Date output format
 * @param {string} empty Value to return when input is invalid
 * @return {*} Value provided in `empty` param, or the formatted time
 */
export function formatDateFromDifference(
  difference: Duration | null | undefined,
  format = DEFAULT_TIME_FORMAT,
  empty = "N/A",
): string {
  if (!difference || !difference.isValid()) {
    return empty;
  }

  return moment().add(difference).format(format);
}

export function formatDateWithAllTimezones(
  dateStr: MomentInput | null | undefined,
  format = DEFAULT_TIME_FORMAT,
  empty = "N/A",
): string {
  // This formatter takes timestamp which has no timezone and is relative to
  // device's local clock.
  return dateStr
    ? `${dateStringToDateFormat(dateStr, format)} (All Timezones)`
    : empty;
}

/**
 * Parses a provided time in UTC to a specified timezone.
 * @param {string | Object} dateStr Date string or moment-like object to be
 * parsed
 * @param {string} timezone Timezone string or UTC offset
 * @param {string} empty Value to return when input is invalid
 * @return {*} Value provided in `empty` param, or the parsed object
 */
export function parseDateWithTimezoneOrOffset(
  dateStr: MomentInput | null | undefined,
  timezone: string | null | undefined,
  empty = "N/A",
): Moment | string {
  if (!dateStr || !timezone) {
    return empty;
  }

  if (moment.tz.zone(timezone)) {
    return moment.utc(dateStr).tz(timezone);
  }

  return moment.utc(dateStr).utcOffset(timezone);
}

export function timeSince(
  utcTime: MomentInput | null | undefined,
  suffix = false,
  def = "N/A",
): string {
  if (utcTime) {
    const utc = moment.utc(utcTime);
    const diff = utc.diff(moment.utc());
    return moment.duration(diff).humanize(suffix);
  }

  return def;
}

/**
 * Calculates the time difference between a specified timezone and local.
 * @param {string | Object} time Date string or moment-like object to be parsed
 * @param {string} timezone Timezone string or UTC offset
 * @param {string} empty Value to return when input is invalid
 * @return {*} Value provided in `empty` param, or difference between parsed
 * time and local time
 */
export function timeDifferenceWithTimezone(
  time: MomentInput | null | undefined,
  timezone: string | null | undefined,
  empty: null = null,
): Duration | null {
  if (!time || !timezone) {
    return empty;
  }

  const parsedDate = parseDateWithTimezoneOrOffset(time, timezone);
  if (typeof parsedDate === "string") {
    return empty;
  }
  const local = moment(parsedDate).local();
  const diff = parsedDate.local(true).diff(local);

  return moment.duration(diff);
}

export function utcToLocal(
  utcTime: MomentInput | null | undefined,
  def = "N/A",
): string {
  return utcToLocalFormat(utcTime, DEFAULT_TIME_FORMAT, def);
}

export function utcToLocalFormat(
  utcTime: MomentInput | null | undefined,
  format = DEFAULT_TIME_FORMAT,
  def = "N/A",
): string {
  if (utcTime) {
    // By utilizing moment.tz we can format timezone with formatting token 'z',
    // otherwise it will show nothing.
    const utc = moment.tz(utcTime, "UTC");
    const local = utc.tz(moment.tz.guess());
    return local.format(format);
  }

  return def;
}

export function durationToHuman(msDuration: number | null | undefined): string {
  if (typeof msDuration !== "number") {
    return "N/A";
  }

  const duration = moment.duration(msDuration);

  const hours = duration.hours();
  const minutes = duration.minutes();
  const seconds = duration.seconds();

  const h = hours ? hours + " h" : null;
  const m = minutes ? minutes + " m" : null;
  const s = seconds ? seconds + " s" : null;

  return [h, m, s].filter((t) => t).join(" ");
}
